/**
 * ui-future deck model (Control Room v4): five card decks on one carousel.
 * MENU (7) → PROJECTS (6 groups) → per-business TOOL decks · BEAST (3) · EMAIL (4).
 * Built from the SAME registry as the classic UI — single source of truth.
 */
import { BUSINESS_PROJECTS, isPlaceholderCollab, type BusinessProject } from '@/lib/business-projects';

export const BIZ_COLOR: Record<string, string> = {
  wtf: 'var(--uif-biz-wv)', charters: 'var(--uif-biz-pc)', olga: 'var(--uif-biz-on)',
  astro: 'var(--uif-biz-af)', vctronics: 'var(--uif-biz-vc)', collabs: 'var(--uif-biz-co)',
};
export const BIZ_SIGIL: Record<string, string> = {
  wtf: 'WV', charters: 'PC', olga: 'ON', astro: 'AF', vctronics: 'VC', collabs: 'CO',
};

export type Card = {
  id: string;            // unique within deck
  title: string;
  sub?: string;
  sigil?: string;
  color?: string;
  opens: { deck?: string } | { surface: string; ctx?: Record<string, string> };
};

const collabPeople = BUSINESS_PROJECTS.filter(p => isPlaceholderCollab(p.slug));
const mainProjects = BUSINESS_PROJECTS.filter(p => !isPlaceholderCollab(p.slug));

const TOOL_LABEL: Record<string, string> = {
  performance: 'PERFORMANCE', improve: 'IMPROVE', factory: 'FACTORY', funnel: 'FUNNEL',
  'video-lab': 'VIDEO LAB', 'ab-tests': 'A/B TESTS', ads: 'ADS', websites: 'WEBSITES',
  content: 'CONTENT', crm: 'CRM', email: 'EMAIL', investors: 'INVESTORS',
};

export function toolDeck(p: BusinessProject): Card[] {
  const color = BIZ_COLOR[p.slug] || 'var(--uif-biz-co)';
  const overview: Card[] = p.funnelId
    ? [{ id: 'overview', title: 'OVERVIEW', sub: 'KPI cockpit', color, opens: { surface: `overview:${p.slug}` } }]
    : [];
  return [
    ...overview,
    ...p.tabs.map(t => ({
      id: t, title: TOOL_LABEL[t] || t.toUpperCase(), color,
      sub: '',
      opens: { surface: `${t}:${p.slug}` },
    })),
  ];
}

export const DECKS: Record<string, Card[]> = {
  menu: [
    { id: 'beast', title: 'Beast', sub: 'Brain · Network · Portfolio', sigil: 'B', opens: { deck: 'beast' } },
    { id: 'projects', title: 'Projects', sub: `${mainProjects.length + 1} business groups`, sigil: 'P', opens: { deck: 'projects' } },
    { id: 'content', title: 'Content', sub: 'content studios', sigil: 'C', opens: { surface: 'content' } },
    { id: 'crm', title: 'CRM', sub: 'leads across businesses', sigil: 'L', opens: { surface: 'crm' } },
    { id: 'email', title: 'Email', sub: 'Inbox · Compose · Contacts · Senders', sigil: 'E', opens: { deck: 'email' } },
    { id: 'chat', title: 'Chat', sub: 'parallel Soma sessions', sigil: 'S', opens: { surface: 'chat' } },
    { id: 'settings', title: 'Settings', sub: 'engineering calibration', sigil: '⚙', opens: { surface: 'settings' } },
  ],
  projects: [
    ...mainProjects.map(p => ({
      id: p.slug, title: p.name.toUpperCase(), sub: p.tagline,
      sigil: BIZ_SIGIL[p.slug], color: BIZ_COLOR[p.slug],
      opens: { deck: `tools:${p.slug}` },
    })),
    {
      id: 'collabs', title: 'COLLABS', sub: `${collabPeople.length} people`,
      sigil: 'CO', color: BIZ_COLOR.collabs, opens: { deck: 'tools:collabs' },
    },
  ],
  beast: [
    { id: 'brain', title: 'Brain', sub: 'sessions · muscles · protocols', opens: { surface: 'brain' } },
    { id: 'network', title: 'Network', sub: 'neural web of the empire', opens: { surface: 'network' } },
    { id: 'sessions', title: 'Sessions', sub: 'parallel Soma minds', opens: { surface: 'sessions' } },
    { id: 'portfolio', title: 'Portfolio', sub: 'every website', opens: { surface: 'portfolio' } },
  ],
  email: [
    { id: 'inbox', title: 'Inbox', sub: 'all businesses', opens: { surface: 'inbox' } },
    { id: 'compose', title: 'Compose', sub: 'send as any business', opens: { surface: 'compose' } },
    { id: 'contacts', title: 'Contacts', sub: 'outreach book', opens: { surface: 'contacts' } },
    { id: 'senders', title: 'Senders', sub: 'identities & domains', opens: { surface: 'senders' } },
  ],
  'tools:collabs': collabPeople.map(p => ({
    id: p.slug, title: p.name.toUpperCase(), sub: p.sites !== 'factory' && p.sites[0] ? p.sites[0].domain : 'no assets attached yet',
    sigil: p.name.slice(0, 2).toUpperCase(), color: BIZ_COLOR.collabs,
    opens: { deck: `tools-person:${p.slug}` },
  })),
};

// tool decks for main projects + collab people
for (const p of mainProjects) DECKS[`tools:${p.slug}`] = toolDeck(p);
for (const p of collabPeople) DECKS[`tools-person:${p.slug}`] = toolDeck(p);

/**
 * VOICE CATALOG (Boss 2026-07-13 'control beast with voice'): a compact, live list of every
 * navigable destination, injected into the voice model's prompt so it maps Boss's words to a
 * REAL target id instead of guessing. The Shell resolver matches an emitted id exactly (score 100),
 * so a correct id from this list = deterministic navigation. Grouped by deck for the model to read.
 */
export function voiceCatalog(): string {
  const lines: string[] = [];
  const seen = new Set<string>();
  const order = ['menu', 'beast', 'email', 'projects'];
  const emit = (deckKey: string) => {
    const cards = DECKS[deckKey];
    if (!cards || !cards.length) return;
    const title = DECK_TITLE[deckKey] || deckKey.toUpperCase();
    const items = cards.map(c => `${c.id} (${c.title})`).join(', ');
    lines.push(`- ${title}: ${items}`);
    seen.add(deckKey);
  };
  order.forEach(emit);
  // per-project tool decks (performance, ads, factory, etc.)
  Object.keys(DECKS).filter(k => k.startsWith('tools:') || k.startsWith('tools-person:'))
    .forEach(k => { if (!seen.has(k)) emit(k); });
  return lines.join('\n');
}

export const DECK_TITLE: Record<string, string> = {
  menu: 'COMMAND MENU', projects: 'PROJECTS', beast: 'BEAST', email: 'EMAIL',
  ...Object.fromEntries(mainProjects.map(p => [`tools:${p.slug}`, p.name.toUpperCase()])),
  'tools:collabs': 'COLLABS',
  ...Object.fromEntries(collabPeople.map(p => [`tools-person:${p.slug}`, p.name.toUpperCase()])),
};
